import axios from 'axios'
import { DocumentDrainResult, DocumentJobProducer, DocumentSource } from '@numeo/types-core'

/** The result of picking (Tabs 1/2) or uploading (Tab 3) a document — always a REAL contentHash/blobUri pair, never hand-typed. */
export interface SelectedDocument {
    contentHash: string
    blobUri: string
    filename?: string
    projectName?: string
}

export interface PipelineJobRow {
    id: string
    content_hash: string
    project_name: string
    filename: string | null
    blob_uri: string
    doc_type_hint: string | null
    source: string
    status: string
    mode: string
    enqueued_at: string
}

/** Every document source the "existing documents" picker can list — real data already in this system's own DB/GCS. */
export type ExistingDocumentSource = 'ratecon' | 'cdl' | 'bol' | 'pod' | 'seal' | 'trailer' | 'maintenance-invoice'

export const EXISTING_DOCUMENT_SOURCES: { value: ExistingDocumentSource; label: string; requiresProjectName: boolean }[] = [
    { value: 'ratecon', label: 'Rate Confirmation', requiresProjectName: false },
    { value: 'cdl', label: 'CDL (Driver License)', requiresProjectName: false },
    { value: 'bol', label: 'Bill of Lading', requiresProjectName: true },
    { value: 'pod', label: 'Proof of Delivery', requiresProjectName: true },
    { value: 'seal', label: 'Trailer Seal', requiresProjectName: true },
    { value: 'trailer', label: 'Trailer Photo', requiresProjectName: true },
    { value: 'maintenance-invoice', label: 'Maintenance Invoice', requiresProjectName: true },
]

export interface ExistingDocumentRow {
    id: string
    fileUrl: string
    label: string
    date: number
    projectName?: string
}

interface UploadResponse {
    gcsUri: string
    contentHash: string
    filename: string
    mimeType: string
    sizeBytes: number
}

/** The message to show for a failed call: the server's own message when it sent one, else the fallback. */
/** Re-exported, not re-implemented — see `@/api/errorMessage`. Behaviour is unchanged. */
export { apiErrorMessage } from '@/api/errorMessage'

export const documentProcessingTestApi = {
    async trigger(input: { projectName: string; blobUri: string; source: DocumentSource; docTypeHint?: string; skipOcr?: boolean }) {
        const res = await axios.post('/gluon/document-processing-test/trigger/v1', input)
        return res.data?.data
    },

    async drain(): Promise<DocumentDrainResult> {
        const res = await axios.post('/gluon/document-processing-test/drain/v1', {})
        return res.data?.data
    },

    async listPipelineJobs(params: { projectName?: string; docTypeHint?: string; pageSize?: number }): Promise<PipelineJobRow[]> {
        const query = new URLSearchParams()
        // Only what this page submitted: every job lives in Document Jobs.
        query.set('producer', DocumentJobProducer.GLUON_TEST)
        if (params.projectName) query.set('projectName', params.projectName)
        if (params.docTypeHint) query.set('docTypeHint', params.docTypeHint)
        query.set('pageSize', String(params.pageSize ?? 25))
        query.set('page', '1')
        const res = await axios.get(`/gluon/document-jobs/v1?${query.toString()}`)
        return res.data?.data?.rows ?? []
    },

    async listExistingDocuments(source: ExistingDocumentSource, projectName?: string, limit = 15): Promise<ExistingDocumentRow[]> {
        const query = new URLSearchParams({ source, limit: String(limit) })
        if (projectName) query.set('projectName', projectName)
        const res = await axios.get(`/gluon/document-processing-test/existing-documents/v1?${query.toString()}`)
        return res.data?.data?.rows ?? []
    },

    async resolveExternal(fileUrl: string, projectName: string): Promise<{ contentHash: string; blobUri: string }> {
        const res = await axios.post('/gluon/document-processing-test/resolve-external/v1', { fileUrl, projectName })
        return res.data?.data
    },

    async uploadDocument(file: File, projectName: string): Promise<UploadResponse> {
        const formData = new FormData()
        formData.append('file', file)
        const res = await axios.post(`/gluon/document-processing-test/upload/v1?projectName=${encodeURIComponent(projectName)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 330000,
        })
        return res.data?.data
    },
}
